import { NavLink, Outlet } from "react-router-dom";
import clsx from "clsx";
import { useAuth } from "../auth/AuthProvider";
import { useBranding } from "../lib/useBranding";
import {
  IconHome,
  IconUsers,
  IconMoney,
  IconSettings,
  IconLogout,
  IconChart,
  IconHelp,
} from "./icons";

interface NavItem {
  to: string;
  label: string;
  icon: (p: any) => JSX.Element;
  end?: boolean;
}

export function Layout() {
  const { me, signOut } = useAuth();
  const isAdmin = me?.role === "admin";

  const nav: NavItem[] = isAdmin
    ? [
        { to: "/", label: "Overview", icon: IconHome, end: true },
        { to: "/clients", label: "Clients", icon: IconUsers },
        { to: "/revenue", label: "Revenue", icon: IconMoney },
        { to: "/settings", label: "Settings", icon: IconSettings },
        { to: "/docs", label: "Guide", icon: IconHelp },
      ]
    : [
        { to: `/clients/${me?.clientId ?? ""}`, label: "My performance", icon: IconChart },
        { to: "/docs", label: "Guide", icon: IconHelp },
      ];

  return (
    <div className="flex min-h-screen bg-plane">
      {/* Sidebar (md+) */}
      <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col border-r border-hairline bg-surface md:flex">
        <Brand />
        <nav className="flex flex-1 flex-col gap-1 p-3">
          {nav.map((item) => (
            <NavItemLink key={item.to} item={item} />
          ))}
        </nav>
        <UserFooter email={null} role={me?.role ?? ""} onSignOut={signOut} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top nav */}
        <header className="flex items-center justify-between border-b border-hairline bg-surface px-4 py-3 md:hidden">
          <Brand compact />
          <button onClick={signOut} className="btn-ghost" title="Sign out">
            <IconLogout />
          </button>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-hairline bg-surface px-3 py-2 md:hidden">
          {nav.map((item) => (
            <NavItemLink key={item.to} item={item} />
          ))}
        </nav>

        <main className="mx-auto w-full max-w-7xl flex-1 overflow-x-clip p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Brand({ compact }: { compact?: boolean }) {
  const { logo, title, subtitle } = useBranding();
  return (
    <div className={clsx("flex items-center gap-2 px-4", compact ? "" : "h-16 border-b border-hairline")}>
      {logo ? (
        <img src={logo} alt={title} className="h-7 w-7 rounded-md object-contain" />
      ) : (
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand text-sm font-bold text-white">
          L
        </div>
      )}
      <div className="leading-tight">
        <div className="text-sm font-semibold text-ink">{title}</div>
        {!compact && (
          <div className="text-[10px] uppercase tracking-wide text-muted">{subtitle}</div>
        )}
      </div>
    </div>
  );
}

function NavItemLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        clsx(
          "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          isActive ? "bg-surface-3 text-ink" : "text-muted hover:bg-surface-2 hover:text-ink"
        )
      }
    >
      <Icon width={16} height={16} />
      {item.label}
    </NavLink>
  );
}

function UserFooter({
  role,
  onSignOut,
}: {
  email: string | null;
  role: string;
  onSignOut: () => void;
}) {
  return (
    <div className="border-t border-hairline p-3">
      <div className="mb-2 px-1 text-xs text-muted">
        Signed in as <span className="font-medium capitalize text-ink-secondary">{role}</span>
      </div>
      <button onClick={onSignOut} className="btn-ghost w-full">
        <IconLogout width={16} height={16} /> Sign out
      </button>
    </div>
  );
}
